import { api } from "@/lib/api";
import type { WorkbookLanguageVariantDraft } from "@/lib/schema";
import { StudioContentStatus } from "../schema";

export interface CreateWorkbookPayload {
  categoryId?: string;
  tagOptionIds?: string[];
  baseLanguage?: string;
  internalNotes?: string;
  languages: WorkbookLanguageVariantDraft[];
}

export interface UpdateWorkbookPayload {
  categoryId?: string;
  tagOptionIds?: string[];
  internalNotes?: string;
  languages?: WorkbookLanguageVariantDraft[];
  blocks?: any[]; // Existing shape kept; tighten in a follow-up.
}

const CONTENT_WORKBOOK = "/content-workbooks";
const CONTENT_WORKBOOK_BLOCKS = "/content-workbook-blocks";

export const ContentWorkbookService = {
  async findAll(filters?: {
    search?: string;
    status?: StudioContentStatus;
    categoryId?: string;
    tagId?: string;
    language?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: any[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.tagId) params.set("tagId", filters.tagId);
    if (filters?.language) params.set("language", filters.language);
    if (filters?.page) params.set("page", filters.page.toString());
    if (filters?.limit) params.set("limit", filters.limit.toString());

    return api.get<{ items: any[]; pagination: any }>(
      `${CONTENT_WORKBOOK}?${params.toString()}`,
    );
  },

  async findOne(id: string): Promise<any> {
    return api.get<any>(`${CONTENT_WORKBOOK}/${id}`);
  },

  async findByVersionId(versionId: string): Promise<any> {
    return api.get<any>(`${CONTENT_WORKBOOK}/version/${versionId}`);
  },

  async create(payload: any): Promise<any> {
    return api.post<any>(CONTENT_WORKBOOK, payload);
  },

  async update(id: string, payload: any): Promise<any> {
    return api.patch<any>(`${CONTENT_WORKBOOK}/${id}`, payload);
  },

  async submitForReview(id: string): Promise<any> {
    return api.post(`${CONTENT_WORKBOOK}/${id}/submit`, {});
  },

  async createVersion(
    id: string,
  ): Promise<{ id: string; version: number; versionId?: string }> {
    return api.post(`${CONTENT_WORKBOOK}/${id}/create-version`, {});
  },

  async archive(id: string): Promise<any> {
    return api.delete<any>(`${CONTENT_WORKBOOK}/${id}`);
  },

  async restore(id: string): Promise<any> {
    return api.post<any>(`${CONTENT_WORKBOOK}/${id}/restore`, {});
  },

  async createBlock(workbookId: string, payload: any): Promise<any> {
    return api.post<any>(`${CONTENT_WORKBOOK}/${workbookId}/blocks`, payload);
  },

  async createBlockVariant(blockId: string, payload: any): Promise<any> {
    return api.post<any>(
      `${CONTENT_WORKBOOK_BLOCKS}/${blockId}/variants`,
      payload,
    );
  },
};
